<template>
    <button
        type="button"
        :class="getClass(localState)"
        :title="localTitle"
        :disabled="localIsDisabled"
    >
        <component
            :is="localIcon"
            v-if="typeof localIcon === 'function'"
            :class="config.icon[localSize]"
        />
        <span>{{ localValue }}</span>
    </button>
</template>
<script setup>
import { ref, watch } from 'vue';

defineExpose({
    name: 'ActionButton',
});

const props = defineProps({
    state: {
        type: String,
        default: '',
    },
    icon: {
        type: Function,
        default: () => {
            return false;
        },
    },
    value: {
        type: String,
        default: '',
    },
    title: {
        type: String,
        default: '',
    },
    isDisabled: {
        type: Boolean,
        default: false,
    },
    size: {
        type: String,
        default: 'sm',
    },
    classOverride: {
        type: String,
        default: '',
    },
});

const ButtonState = [
    'brand',
    'promo',
    'primary',
    'info',
    'success',
    'warning',
    'danger',
    'neutral',
    'clean',
    'transparent',
];
const ButtonSize = ['xs', 'sm', 'md', 'lg', 'xl'];
/* const SwitchButtonState = [
    'brand',
    'promo',
    'primary',
    'info',
    'success',
    'warning',
    'danger',
    'neutral',
]; */

const localState = ButtonState.includes(props.state) ? ref(props.state) : ref('neutral');
const localIcon = ref(props.icon);
const localValue = ref(props.value);
const localTitle = ref(props.title);
const localIsDisabled = ref(props.isDisabled);
const localSize = ButtonSize.includes(props.size) ? ref(props.size) : ref('sm');
const localClassOverride = ref(props.classOverride);

const config = {
    default:
        'transition-all select-none rounded-md border border-transparent rounded-md inline-flex gap-1 justify-center items-center outline-0 outline-hidden focus:outline-hidden focus:outline-0 font-medium min-w-fit focus:ring-offset-gray-100 dark:focus:ring-offset-gray-900 text-left',
    icon: {
        xs: 'w-3 h-3 -mt-0.5',
        sm: 'w-4 h-4 -mt-0.5',
        md: 'w-5 h-5 -mt-0.5',
        lg: 'w-6 h-6 -mt-0.5',
        xl: 'w-7 h-7 -mt-0.5',
    },
    size: {
        xs: 'text-xs py-1 px-1',
        sm: 'text-sm py-1 px-2',
        md: 'text-base py-1 px-2',
        lg: 'text-lg py-1 px-2',
        xl: 'text-xl py-1 px-2',
    },
    state: {
        brand: {
            default:
                'bg-gradient-to-tr from-emerald-400 via-blue-500 to-indigo-600 hover:from-emerald-500 hover:via-sky-600 hover:to-blue-700 focus:ring-blue-400 text-white dark:from-emerald-600 dark:via-blue-700 dark:to-indigo-700 dark:hover:from-emerald-700 dark:hover:via-sky-800 dark:hover:to-blue-800 dark:focus:ring-blue-800 dark:text-gray-200 shadow-sm focus:shadow-sm focus:ring-2 focus:ring-offset-2 cursor-pointer',
            disabled:
                'bg-gray-200 dark:bg-gray-600 hover:bg-gray-200 hover:dark:bg-gray-600 focus:ring-transparent text-gray-400 cursor-not-allowed shadow-sm focus:shadow-sm',
        },
        promo: {
            default:
                'bg-violet-500 hover:bg-violet-600 focus:ring-violet-400 text-white shadow-sm focus:shadow-sm focus:ring-2 focus:ring-offset-2 cursor-pointer',
            disabled:
                'bg-gray-200 dark:bg-gray-600 hover:bg-gray-200 hover:dark:bg-gray-600 focus:ring-transparent text-gray-400 cursor-not-allowed shadow-sm focus:shadow-sm',
        },
        primary: {
            default:
                'bg-blue-500 hover:bg-blue-600 focus:ring-blue-400 text-white shadow-sm focus:shadow-sm focus:ring-2 focus:ring-offset-2 cursor-pointer',
            disabled:
                'bg-gray-200 dark:bg-gray-600 hover:bg-gray-200 hover:dark:bg-gray-600 focus:ring-transparent text-gray-400 cursor-not-allowed shadow-sm focus:shadow-sm',
        },
        info: {
            default:
                'bg-cyan-500 hover:bg-cyan-600 focus:ring-cyan-400 text-white dark:bg-cyan-600 dark:hover:bg-cyan-800 dark:focus:ring-cyan-600 dark:text-gray-100 shadow-sm focus:shadow-sm focus:ring-2 focus:ring-offset-2 cursor-pointer',
            disabled:
                'bg-gray-200 dark:bg-gray-600 hover:bg-gray-200 hover:dark:bg-gray-600 focus:ring-transparent text-gray-400 cursor-not-allowed shadow-sm focus:shadow-sm',
        },
        success: {
            default:
                'bg-emerald-500 hover:bg-emerald-600 focus:ring-emerald-400 text-white shadow-sm focus:shadow-sm focus:ring-2 focus:ring-offset-2 cursor-pointer',
            disabled:
                'bg-gray-200 dark:bg-gray-600 hover:bg-gray-200 hover:dark:bg-gray-600 focus:ring-transparent text-gray-400 cursor-not-allowed shadow-sm focus:shadow-sm',
        },
        warning: {
            default:
                'bg-amber-500 hover:bg-amber-600 focus:ring-amber-400 text-white shadow-sm focus:shadow-sm focus:ring-2 focus:ring-offset-2 cursor-pointer',
            disabled:
                'bg-gray-200 dark:bg-gray-600 hover:bg-gray-200 hover:dark:bg-gray-600 focus:ring-transparent text-gray-400 cursor-not-allowed shadow-sm focus:shadow-sm',
        },
        danger: {
            default:
                'bg-red-500 hover:bg-red-600 focus:ring-red-400 text-white shadow-sm focus:shadow-sm focus:ring-2 focus:ring-offset-2 cursor-pointer',
            disabled:
                'bg-gray-200 dark:bg-gray-600 hover:bg-gray-200 hover:dark:bg-gray-600 focus:ring-transparent text-gray-400 cursor-not-allowed shadow-sm focus:shadow-sm',
        },
        neutral: {
            default:
                'bg-neutral-500 hover:bg-neutral-600 focus:ring-neutral-400 text-white shadow-sm focus:shadow-sm focus:ring-2 focus:ring-offset-2 cursor-pointer',
            disabled:
                'bg-gray-200 dark:bg-gray-600 hover:bg-gray-200 hover:dark:bg-gray-600 focus:ring-transparent text-gray-400 cursor-not-allowed shadow-sm focus:shadow-sm2',
        },
        clean: {
            default:
                'bg-white dark:bg-gray-900 focus:ring-neutral-400 text-gray-500 hover:text-gray-800  dark:text-gray-300 dark:hover:text-gray-400 shadow-sm focus:shadow-sm focus:ring-2 focus:ring-offset-2 cursor-pointer',
            disabled:
                'bg-white dark:bg-gray-900 focus:ring-transparent text-gray-400 hover:text-gray-400 cursor-not-allowed shadow-sm focus:shadow-sm',
        },
        transparent: {
            default:
                'bg-transparent ring-0 ring-transparent ring-offset-0 outline-hidden outline-0 focus:outline-hidden focus:outline-0 focus:ring-0 focus:ring-offset-0 text-gray-500 hover:text-gray-600 dark:text-gray-400 dark:hover:text-gray-300 cursor-pointer',
            disabled:
                'bg-transparent dark:bg-transparent focus:ring-transparent text-gray-400 cursor-not-allowed shadow-sm focus:shadow-sm',
        },
    },
};

const getClass = (state) => {
    return `${config.default} ${config.size[localSize.value]} ${
        config.state[state][localIsDisabled.value ? 'disabled' : 'default']
    } ${localClassOverride.value}`;
};

watch(
    () => props.state,
    (newValue) => {
        localState.value = newValue;
    },
);
watch(
    () => props.icon,
    (newValue) => {
        localIcon.value = newValue;
    },
);
watch(
    () => props.value,
    (newValue) => {
        localValue.value = newValue;
    },
);
watch(
    () => props.title,
    (newValue) => {
        localTitle.value = newValue;
    },
);
watch(
    () => props.isDisabled,
    (newValue) => {
        localIsDisabled.value = newValue;
    },
);
watch(
    () => props.size,
    (newValue) => {
        localSize.value = newValue;
    },
);
watch(
    () => props.classOverride,
    (newValue) => {
        localClassOverride.value = newValue;
    },
);
</script>
